'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import Navbar from '@/components/common/Navbar';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export default function FAQPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...Array.from(new Set(faqs.map(faq => faq.category)))];
  const filteredFaqs = activeCategory === 'All' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFaq = (id: string) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-green-50/30">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
            <p className="text-xl text-white/90">Find answers to common questions about our services, pricing, and scheduling.</p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 section-gradient-mid dot-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeCategory === category
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-green-50 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Icon name="ArrowPathIcon" size={40} className="animate-spin text-green-600 mx-auto" />
              <p className="text-muted-foreground mt-4">Loading FAQs...</p>
            </div>
          )}

          {/* FAQ List */}
          {!loading && (
            <div className="max-w-4xl mx-auto space-y-4">
              {filteredFaqs.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="QuestionMarkCircleIcon" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-muted-foreground">No FAQs found in this category.</p>
                </div>
              ) : (
                filteredFaqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-white rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg"
                  >
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-green-50 transition-colors"
                    >
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <Icon name="QuestionMarkCircleIcon" size={20} className="text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground">{faq.question}</h3>
                          <span className="text-xs text-green-600 font-medium mt-1 inline-block">
                            {faq.category}
                          </span>
                        </div>
                      </div>
                      <Icon
                        name={openFaqId === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={24}
                        className="text-gray-400 flex-shrink-0"
                      />
                    </button>
                    {openFaqId === faq.id && (
                      <div className="px-6 pb-5 pt-2">
                        <div className="pl-12">
                          <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32] via-green-600 to-[#4CAF50] text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Still Have Questions?</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Cannot find the answer you are looking for? Our friendly team is here to help.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 bg-white text-green-700 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 transition-all shadow-lg hover:shadow-xl"
          >
            Contact Us
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}